import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MapPin, ExternalLink, Mic2 } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { getList } from "@/lib/api";
import { IMG } from "@/lib/images";
import { PageHeader } from "@/components/Editorial";
import InviteModal from "@/components/InviteModal";

const THEMES = [
  "Histoire de l'ésotérisme", "Histoire des arts divinatoires", "Ésotérisme et histoire de France",
  "Anecdotes historiques étranges", "Occultisme et personnages historiques", "Mysticisme",
  "Traditions", "Symbolique", "Pierres et couronnes royales", "Médiumnité", "Deuil",
  "Signes et croyances autour de la mort", "Rêves", "Onirothérapie",
];

function ConfCard({ c }) {
  return (
    <div className="dl-card overflow-hidden" data-testid={`conf-${c.id}`}>
      {c.image_url && <img src={c.image_url} alt={c.title} className="w-full h-52 object-cover" loading="lazy" />}
      <div className="p-7">
        {c.date && <div className="dl-eyebrow mb-2" style={{ fontSize: "0.6rem" }}>{c.date}</div>}
        <h3 className="serif text-2xl text-[#F3E9D2]">{c.title}</h3>
        {c.location && <p className="text-sm text-[#F3E9D2]/55 mt-2 inline-flex items-center gap-1.5"><MapPin size={13} /> {c.location}</p>}
        {c.description && <p className="text-sm text-[#F3E9D2]/70 mt-3">{c.description}</p>}
        {c.link && <a href={c.link} target="_blank" rel="noreferrer" className="dl-link text-sm mt-4 inline-flex items-center gap-1">En savoir plus <ExternalLink size={12} /></a>}
      </div>
    </div>
  );
}

export default function Conferences() {
  const [invite, setInvite] = useState(false);
  const { data: confs = [] } = useQuery({ queryKey: ["conferences"], queryFn: () => getList("conferences") });

  useSeo({
   title: "Conférences médiumnité & ésotérisme | Dorothée Lancelot",
description: "Découvrez les conférences et interventions de Dorothée Lancelot autour de la médiumnité, du deuil, de l’ésotérisme, de l’histoire et des croyances.",
    path: "/conferences",
    keywords: ["conférencière ésotérisme", "conférence médiumnité", "conférence sur le deuil", "inviter une conférencière", "intervention salon ésotérisme", "conférence spiritualité", "conférence rêves et symbolisme"],
  });

  const upcoming = confs.filter((c) => c.type === "upcoming");
  const past = confs.filter((c) => c.type !== "upcoming");

  return (
    <div>
      <PageHeader
        eyebrow="Interventions publiques"
        title="Conférences & interventions"
        intro="Dorothée Lancelot intervient lors de salons, conférences, rencontres et événements — pour raconter l'histoire de l'invisible avec rigueur et curiosité."
        image={IMG.conference1}
      />

      <section className="dl-container py-16">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div className="dl-eyebrow">Thématiques disponibles</div>
          <button data-testid="conf-invite-btn" onClick={() => setInvite(true)} className="dl-btn dl-btn-solid"><Mic2 size={15} /> Inviter Dorothée en conférence</button>
        </div>
        <div className="flex flex-wrap gap-2.5 mb-16">
          {THEMES.map((t, i) => (
            <span key={i} className="px-4 py-2 text-sm border border-[rgba(198,167,106,0.25)] text-[#F3E9D2]/75">{t}</span>
          ))}
        </div>

        {upcoming.length > 0 && (
          <div className="mb-16">
            <h2 className="serif text-3xl text-[#F3E9D2] mb-8">Conférences à venir</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcoming.map((c) => <ConfCard key={c.id} c={c} />)}
            </div>
          </div>
        )}

        {past.length > 0 && (
          <div>
            <h2 className="serif text-3xl text-[#F3E9D2] mb-8">Conférences passées</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {past.map((c) => <ConfCard key={c.id} c={c} />)}
            </div>
          </div>
        )}
      </section>
      <InviteModal open={invite} onClose={() => setInvite(false)} />
    </div>
  );
}
